import { useMemo, useEffect } from 'react';

import { useDLMApplicationView } from "../context/DLMApplicationViewProvider"
import { convertDLMApp2DOCDetails, convertDLMApp2ROIDetails } from '../_helpers';

import { 
  ApplicationDetails, DOCDetails, DOCSplitSiteDetails, ROITargets, SiteItemDetails, Sites, SupportingDocuments 
} from './detail-panels'

import { filter, findIndex, orderBy, sortBy } from 'lodash';
import { ROIDetails } from './detail-panels/ROIDetails';
import { ROIItems } from './detail-panels/ROIItems';
import { isPopulatedArray } from '../../../../components/helpers';
import { useAuth } from '../../../auth';
    
const DLMApplicationDetails = () => {

  const { hasRole } = useAuth();

  const { dlmAppData, setSiteIDs, setSiteIndex, selectedSite, setSelectedSite } = useDLMApplicationView();
  
  const { applicationType, roiAppData, isDoor } = useMemo(() => {
    if (dlmAppData === undefined) return { applicationType: '', roiAppData : null, isDoor : false};

    const { applicationType, dlmType } = dlmAppData;        
    const isDoor = dlmType?.door ?? false;

    const roiAppData = (applicationType === 'TYPE_ROIEDIT' || applicationType === 'TYPE_ROISPLIT' || applicationType === 'TYPE_LABELREPLACEMENT') ? convertDLMApp2ROIDetails(dlmAppData) : null;

    return { applicationType, roiAppData, isDoor };

  }, [dlmAppData]);

  const { detailsTitle, itemsTitle } = useMemo(() => {

    let detailsTitle = 'Application Details';
    let itemsTitle = 'Item Details';
    
    switch(applicationType) {        
      case 'TYPE_ROIEDIT':
        detailsTitle = 'Revise ROI';
        itemsTitle = 'Revise Items';        
        break;
      case 'TYPE_ROISPLIT': 
        detailsTitle = 'Split ROI';
        itemsTitle = 'Split Items';        
        break;
      case 'TYPE_LABELREPLACEMENT': 
        detailsTitle = 'Replace ROI';
        itemsTitle = 'Replace Items';        
        break;
      default: 
        break;
    }

    return { detailsTitle, itemsTitle };
  }, [applicationType])

  // select first site
  useEffect(() => {

    if (dlmAppData === undefined) return;

    const { siteApplications } = dlmAppData;
    const siteIDs = siteApplications && siteApplications.length > 0 ? orderBy(filter( siteApplications,  (o) => { return (! o?.deleted); }), ['id'], ['asc']).map(o => o.id) : [];

    if (! (siteIDs && siteIDs.length > 0)) {
      setSiteIndex(-1);
      setSelectedSite(null);
      return;
    }

    setSiteIDs(siteIDs);
    setSiteIndex(0);

    const _idx = findIndex(siteApplications, { id : siteIDs[0]});
    setSelectedSite(siteApplications[_idx]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dlmAppData])


  if (dlmAppData === undefined) {
    return null;
  }

  return(        
  <>
  { /* DOC SPLIT - show sites in separate row*/}
  {(applicationType === 'TYPE_DOCSPLIT' && dlmAppData?.siteApplications && isPopulatedArray(dlmAppData.siteApplications)) && 
  <div className="d-flex flex-row flex-wrap gap-6 mb-4">        
    {sortBy(dlmAppData.siteApplications, ['id']).map((site, idx) => (
      <DOCSplitSiteDetails dlmAppId={dlmAppData.id} key={`siteDetails_panel_${idx}`} site={site} cardTitle={`Site ${idx + 1}`} />    
    ))}
  </div>

  }
  <div className="d-flex flex-row flex-wrap gap-6 mb-4">        
    { /* APPLICATION, SITE INSPECTION, TRIGGERTEST */}
    {(['TYPE_APPLICATION','TYPE_SITEINSPECTION','TYPE_TRIGGERTEST','TYPE_LABELINSPECTION'].includes(applicationType) ) && 
    <>  
    <ApplicationDetails />
    <SupportingDocuments />
    <Sites />
    <SiteItemDetails selectedSite={selectedSite} isDoor={isDoor} />
    </>
    }    

    { /* DOC EDIT, DOC SPLIT */}
    {(['TYPE_DOCEDIT','TYPE_DOCSPLIT','TYPE_DOCINCREASE'].includes(applicationType)) && 
    <DOCDetails dlmAppId={dlmAppData.id} details={convertDLMApp2DOCDetails(dlmAppData)} cardTitle='Application Details' highlightChanges canReplace={hasRole('Super Admin')}/>    
    }
    {(['TYPE_DOCEDIT','TYPE_DOCSPLIT','TYPE_DOCINCREASE'].includes(applicationType) && (dlmAppData?.targetDOC)) &&  
    <DOCDetails dlmAppId={0} details={{...dlmAppData.targetDOC, applicationCertificate: dlmAppData.applicationCertificate}}  cardTitle='Original DOC'/>    
    }

    { /* ROI EDIT, ROI SPLIT, LABEL REPLACEMENT */}
    {((applicationType === 'TYPE_ROIEDIT' || applicationType === 'TYPE_ROISPLIT' || applicationType === 'TYPE_LABELREPLACEMENT') && roiAppData) && 
    <>
    <ROIDetails dlmAppId={dlmAppData.id} details={roiAppData}  cardTitle={detailsTitle} />
    {(applicationType === 'TYPE_ROIEDIT') && 
    <ROIItems details={roiAppData} cardTitle={itemsTitle} isDoor={isDoor} />
    }        
    {(applicationType === 'TYPE_ROISPLIT' || applicationType === 'TYPE_LABELREPLACEMENT' ) && 
    <ROITargets details={roiAppData} cardTitle={itemsTitle} />
    }        
    { (dlmAppData?.targetROI) &&  
    <>
    <ROIDetails dlmAppId={0} details={dlmAppData.targetROI} cardTitle='Original ROI' />
    <ROIItems details={dlmAppData.targetROI} cardTitle="Original Items" isDoor={isDoor} />
    </>
    }
    </>    
    }

    { /* END */}
  </div>
  </>
  )
} 

export { DLMApplicationDetails }